import type { ComponentCheck } from '../hooks/useTierEvaluation';

export interface HostEligibilityCondition {
  type: 'supportedOs' | 'allowedCountry' | 'machineType';
  status: 'pass' | 'fail' | 'unknown' | 'skipped';
  observedValue?: string;
  requirementText?: string;
}

const LABELS: Record<HostEligibilityCondition['type'], string> = {
  supportedOs: 'OS',
  allowedCountry: 'Location',
  machineType: 'Machine type',
};

const OS_COMPONENT = 'osVersion';

function toRow(condition: HostEligibilityCondition): ComponentCheck {
  const passes = condition.status === 'pass';
  return {
    component: condition.type,
    label: LABELS[condition.type] ?? condition.type,
    currentValue: condition.observedValue || 'Unknown',
    score: passes ? 1 : 0,
    requiredScore: 1,
    passes,
    failHint: passes ? undefined : condition.requirementText,
  };
}

/**
 * One checklist for the unlock page.
 *
 * Every disclosed eligibility row stays visible, including a pass, so a rule
 * the machine already meets does not disappear. OS is the exception because
 * the hardware list has its own OS row:
 *   - eligibility OS failing or unknown → that row, and the hardware OS row is dropped
 *   - eligibility OS passing and hardware OS failing → the hardware row
 *   - both passing → one pass row
 */
export function mergeUnlockChecks(
  hardware: ComponentCheck[],
  conditions: HostEligibilityCondition[] | undefined,
): ComponentCheck[] {
  const disclosed = (conditions ?? []).filter((condition) => condition.status !== 'skipped');
  const osEligibility = disclosed.find((condition) => condition.type === 'supportedOs');
  const otherEligibility = disclosed
    .filter((condition) => condition.type !== 'supportedOs')
    .map(toRow);
  const osHardware = hardware.find((check) => check.component === OS_COMPONENT);
  const restHardware = hardware.filter((check) => check.component !== OS_COMPONENT);

  let osRow: ComponentCheck | undefined;
  if (osEligibility && osEligibility.status !== 'pass') {
    osRow = toRow(osEligibility);
  } else if (osEligibility && osHardware && !osHardware.passes) {
    osRow = osHardware;
  } else if (osEligibility) {
    osRow = toRow(osEligibility);
  } else {
    osRow = osHardware;
  }

  const location = otherEligibility.filter((row) => row.component === 'allowedCountry');
  const machineType = otherEligibility.filter((row) => row.component === 'machineType');
  const remainder = otherEligibility.filter(
    (row) => row.component !== 'allowedCountry' && row.component !== 'machineType',
  );

  return [
    ...(osRow ? [osRow] : []),
    ...location,
    ...machineType,
    ...remainder,
    ...restHardware,
  ];
}
